import React, { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';

// Components
import { LocaleProvider } from './components/LocaleProvider';
import { CircleLoader } from './components/common';

// i18n
import { LanguageCode, DEFAULT_LOCALE } from './translations';

// Context
import { useGlobalState } from './context/global-context/hooks';
import {
  setMrmEnvironment,
  setGlobalState,
} from './context/global-context/actions';

// Forms
import { ResumedMembershipForm, TradeUnionPaymentForm, PowerOfAttorneyFormPage, ContactFormPage, RefundFormPage } from './forms';

// Services
import { isMrmProduction } from './services/formService';

// typescript guard for locale
function isLocale(tested: string): tested is LanguageCode {
  return (tested as LanguageCode) !== undefined;
}

interface Props {
  language?: string;
  debug?: boolean;
  formAlias?: string;
  pageId?: number;
}

const App: React.FC<Props> = ({ language, debug, formAlias, pageId }) => {
  // Global state context hook
  const { state, dispatch } = useGlobalState();

  // React query client
  const [queryClient] = useState(() => new QueryClient());

  // Initialize part of the global state
  useEffect(() => {
    if (dispatch) {
      dispatch(
        setGlobalState({
          debug: debug,
          formAlias: formAlias,
          pageId: pageId,
          locale: language,
        })
      );
    }
  }, [debug, formAlias, pageId, language]);

  useEffect(() => {
    isMrmProduction().then((response) => {
      if (dispatch) {
        dispatch(
          setMrmEnvironment(
            response.data.isMrmProduction ? 'production' : 'development'
          )
        );
      }
    });
  }, []);

  const getForm = (formAlias: string): JSX.Element => {
    switch (formAlias) {
      case 'tradeUnionPaymentForm':
        return <TradeUnionPaymentForm />;
      case 'resumedMembershipForm':
        return <ResumedMembershipForm />;
      case 'powerOfAttorneyForm':
        return <PowerOfAttorneyFormPage />
      case 'contactForm':
        return <ContactFormPage />
      case 'refundForm':
        return <RefundFormPage />
      default:
        return <p>No matching form</p>;
    }
  };

  const locale =
    state.locale && isLocale(state.locale) ? state.locale : DEFAULT_LOCALE;

  return (
    <LocaleProvider languageCode={locale}>
      <QueryClientProvider client={queryClient}>
        <div className="form-app">
          {state.mrmEnvironment === 'development' && (
            <div className="form-app__mrm-information-wrapper">
              <div className="form-app__mrm-information">
                <div className="form-app__mrm-information-pill">Mrm Test</div>
              </div>
            </div>
          )}
          {state.formAlias ? getForm(state.formAlias) : <CircleLoader size='sm' />}
          <div id="modal-root"></div>
        </div>
      </QueryClientProvider>
    </LocaleProvider>
  );
};

export default App;
